import type { ReactNode, ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  className?: string;
}

function classes(...list: (string | false | undefined)[]) {
  return list.filter(Boolean).join(' ');
}

export default function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  className = '',
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-2 font-body font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-coffee-400/50';

  const variants = {
    primary:
      'bg-coffee-400 text-white rounded-full hover:bg-coffee-600 active:scale-95 shadow-lg hover:shadow-coffee-400/30',
    outline:
      'border-2 border-coffee-400 text-coffee-400 rounded-full hover:bg-coffee-400 hover:text-white active:scale-95',
    ghost:
      'bg-transparent text-coffee-900 rounded-full hover:bg-coffee-100 active:scale-95',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
  };

  return (
    <button
      className={classes(base, variants[variant], sizes[size], className)}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
      <span>{children}</span>
      {rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
    </button>
  );
}
